import React, { useEffect, useState } from 'react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import api from '@/utils/api';
import { Package, User, Key } from 'lucide-react';
import { toast } from 'sonner';
import ChangePasswordModal from '@/components/ChangePasswordModal';

export default function CustomerDashboard() {
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);
  
  useEffect(() => {
    loadData();
  }, []);
  
  const loadData = async () => {
    try {
      setLoading(true);
      const [userResponse, ordersResponse] = await Promise.all([
        api.get('/auth/me'),
        api.get('/orders')
      ]);
      setUser(userResponse.data);
      setOrders(ordersResponse.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="container mx-auto px-6 py-24 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#334155] border-r-transparent"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-12">
        <h1 
          className="text-4xl md:text-5xl font-bold text-gray-900 mb-8"
          style={{ fontFamily: 'Chivo, sans-serif' }}
          data-testid="dashboard-title"
        >
          My Dashboard
        </h1>
        
        {/* User Info */}
        {user && (
          <div className="bg-gray-50 p-6 rounded-lg border border-[#F3F4F6] mb-12">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
                ) : (
                  <div className="w-16 h-16 bg-[#334155]/10 rounded-full flex items-center justify-center">
                    <User className="h-8 w-8 text-[#334155]" />
                  </div>
                )}
                <div>
                  <h2 className="text-xl font-bold text-gray-900">{user.name}</h2>
                  <p className="text-gray-600">{user.email}</p>
                  {user.role === 'admin' && (
                    <span className="inline-block mt-2 px-3 py-1 bg-[#334155]/10 text-[#334155] text-xs font-bold uppercase tracking-wider rounded-lg">
                      Admin
                    </span>
                  )}
                  {user.is_verified === false && (
                    <span className="inline-block mt-2 px-3 py-1 bg-yellow-500/10 text-yellow-500 text-xs font-bold uppercase tracking-wider rounded-lg">
                      Email Not Verified
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowChangePassword(true)}
                className="h-10 px-6 bg-transparent border border-[#F3F4F6] text-gray-900 font-bold uppercase tracking-wider rounded-lg hover:border-[#334155] hover:text-[#334155] transition-all flex items-center space-x-2 text-sm"
                data-testid="change-password-button"
              >
                <Key className="h-4 w-4" />
                <span>Change Password</span>
              </button>
            </div>
          </div>
        )}
        
        {/* Orders */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Chivo, sans-serif' }}>
            My Orders
          </h2>
          
          {orders.length === 0 ? (
            <div className="bg-gray-50 p-12 rounded-lg border border-[#F3F4F6] text-center" data-testid="no-orders">
              <Package className="h-12 w-12 text-gray-500 mx-auto mb-4" />
              <p className="text-gray-600">No orders yet</p>
            </div>
          ) : (
            <div className="space-y-6">
              {orders.map((order) => (
                <div key={order.order_id} className="bg-gray-50 p-6 rounded-lg border border-[#F3F4F6]" data-testid={`order-${order.order_id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <p className="text-xs text-[#334155] font-bold uppercase tracking-wider mb-1">Order #{order.order_id}</p>
                      <p className="text-gray-600 text-sm">
                        {new Date(order.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider ${
                        order.payment_status === 'paid' ? 'bg-[#334155]/10 text-[#334155]' : 'bg-[#78716C]/10 text-gray-500'
                      }`}>
                        {order.payment_status}
                      </span>
                      <p className="text-gray-900 font-bold mt-2">${order.total.toFixed(2)}</p>
                    </div>
                  </div>
                  
                  <div className="space-y-3 pt-4 border-t border-[#F3F4F6]">
                    {order.items.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between">
                        <p className="text-gray-900">{item.product_name}</p>
                        <p className="text-gray-600 text-sm">${item.total_price.toFixed(2)}</p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-[#F3F4F6]">
                    <span className={`inline-block px-3 py-1 rounded-lg text-xs font-bold uppercase tracking-wider ${
                      order.status === 'delivered' ? 'bg-[#334155]/10 text-[#334155]' :
                      order.status === 'shipped' ? 'bg-blue-500/10 text-blue-500' :
                      order.status === 'processing' ? 'bg-yellow-500/10 text-yellow-500' :
                      'bg-[#78716C]/10 text-gray-500'
                    }`}>
                      Status: {order.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      <ChangePasswordModal 
        isOpen={showChangePassword} 
        onClose={() => setShowChangePassword(false)} 
      />
      
      <Footer />
    </div>
  );
}
